// © 2026 AttainIP Engineering
// Identity API client for OAuth login flow (SPA + PKCE)

import { API_CONFIG } from '@/config/api';

const IDENTITY_BASE = API_CONFIG.identityBaseUrl;

export interface LoginResponse {
  authorization_url: string;
  state: string;
  code_verifier: string;
  token_endpoint: string;
  client_id: string;
  redirect_uri: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: 'Bearer';
  expires_in: number;
}

/**
 * One selectable organisation at the Select-Organisation step (RFC-0510 W05/W28).
 * The identifier is `organization_id`, NOT `id`; `name`/`org_ext_id` are
 * best-effort labels Identity fills in only when it can resolve the org.
 */
export interface OrganizationChoice {
  organization_id: string;
  org_ext_id?: string | null;
  name?: string | null;
  /**
   * The organisation's type from Identity's `OrgType` vocabulary (`personal` |
   * `domain` | `entra_tenant` | `enterprise_domain`) — RFC-0510 W29. A PERSONAL
   * organisation has neither a name nor a readable ext_id, so this is the only
   * thing that can say what the choice is.
   *
   * ⚠️ ADDITIVE, AND ABSENT IS NOT A KIND. An older Identity sends nothing here;
   * `orgChoiceLabel` falls back to the name/ext_id chain rather than guessing.
   */
  org_type?: string | null;
}

/**
 * A login that stopped at Select-Organisation (RFC-0510 W05/W15).
 *
 * Returned INSTEAD of a token pair when the authenticated human holds more than
 * one active membership. It carries NO access token and NO refresh token —
 * nothing has been granted yet.
 *
 * ⚠️ `org_selection_token` GRANTS NOTHING and is short-lived. It must never be
 * handed to `setTokens` (which is where this console reads its session from) and
 * must never be sent as a bearer to anything but the exchange. Before this step
 * existed, `setTokens(tokens.access_token, …)` on this shape stored the string
 * `"undefined"` as the admin token and the sign-in died at `/v1/auth/me`.
 */
export interface OrgSelectionRequiredResponse {
  requires_org_selection: true;
  org_selection_token: string;
  organizations: OrganizationChoice[];
  expires_in: number;
}

/** What a SSO/Azure login can return: a real session, or the picker challenge. */
export type AzureTokenExchangeResult = TokenResponse | OrgSelectionRequiredResponse;

/**
 * Tell the two disjoint login outcomes apart.
 *
 * The challenge has `requires_org_selection: true` and no `access_token`; the
 * token shape has an `access_token` and no `requires_org_selection`. Discriminate
 * on the POSITIVE presence of the discriminator, never on a missing field.
 */
export function requiresOrgSelection(
  result: AzureTokenExchangeResult | null | undefined,
): result is OrgSelectionRequiredResponse {
  return (result as OrgSelectionRequiredResponse | null | undefined)
    ?.requires_org_selection === true;
}

/**
 * Why an org-selection exchange was refused.
 *
 *  - `membership_revoked` (403 `membership_required`) — Identity re-read the
 *    human's ACTIVE memberships and they hold none in the organisation picked.
 *    A revocation between mint and exchange is caught here deliberately; it is
 *    the security property of the feature. Their other choices still stand, so
 *    this returns them to the picker.
 *  - `selection_expired` (400 `invalid_org_selection_token`) — the intermediate
 *    token is no longer honoured. Identity does not distinguish expired from
 *    malformed, and the 10-minute TTL is the realistic cause. Sign in again;
 *    never retried silently.
 */
export type OrgSelectionFailureReason =
  | 'membership_revoked'
  | 'selection_expired'
  | 'unknown';

/** Discriminated outcome of an org-selection exchange. */
export type OrgSelectionExchangeResult =
  | { ok: true; tokens: TokenResponse }
  | { ok: false; reason: OrgSelectionFailureReason; message: string };

/**
 * Read `{code, message}` out of whichever error envelope came back. Identity
 * raises FastAPI `HTTPException(detail=ErrorDetail(...))` → `{"detail": {...}}`;
 * some surfaces wrap it as `{"error": {...}}`, some return it flat.
 */
function readErrorDetail(body: unknown): { code?: string; message?: string } {
  if (!body || typeof body !== 'object') return {};
  const record = body as Record<string, unknown>;
  for (const candidate of [record.detail, record.error, record]) {
    if (candidate && typeof candidate === 'object') {
      const { code, message } = candidate as { code?: unknown; message?: unknown };
      if (typeof code === 'string' || typeof message === 'string') {
        return {
          code: typeof code === 'string' ? code : undefined,
          message: typeof message === 'string' ? message : undefined,
        };
      }
    }
  }
  return {};
}

/**
 * Second half of the Select-Organisation step (RFC-0510 W05/W28).
 * POST /v1/auth/org-selection/exchange
 *
 * The ONLY request `org_selection_token` is ever attached to, and it goes in the
 * request BODY — never as an `Authorization` bearer. Returns a discriminated
 * result rather than throwing, because a refusal is an expected outcome here.
 */
export async function exchangeOrgSelection(
  orgSelectionToken: string,
  organizationId: string,
): Promise<OrgSelectionExchangeResult> {
  let res: Response;
  try {
    res = await fetch(`${IDENTITY_BASE}/v1/auth/org-selection/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        org_selection_token: orgSelectionToken,
        organization_id: organizationId,
      }),
    });
  } catch (err) {
    return {
      ok: false,
      reason: 'unknown',
      message: err instanceof Error ? err.message : 'Could not reach the sign-in service.',
    };
  }

  if (res.ok) return { ok: true, tokens: (await res.json()) as TokenResponse };

  const { code, message } = readErrorDetail(await res.json().catch(() => null));

  // The code is authoritative when Identity sent one; the status is the fallback
  // for anything in front of it (a gateway, a proxy) that answered instead.
  let reason: OrgSelectionFailureReason;
  if (code === 'membership_required') {
    reason = 'membership_revoked';
  } else if (code === 'invalid_org_selection_token') {
    reason = 'selection_expired';
  } else if (res.status === 403) {
    reason = 'membership_revoked';
  } else if (res.status === 400) {
    reason = 'selection_expired';
  } else {
    reason = 'unknown';
  }

  return {
    ok: false,
    reason,
    message: message ?? 'The organisation selection could not be completed.',
  };
}

export interface MeResponse {
  id: string;
  email: string;
  display_name: string;
  organization_id: string;
  roles: string[];
}

export async function getLoginUrl(redirectUri: string): Promise<LoginResponse> {
  const params = new URLSearchParams({ redirect_uri: redirectUri });
  const res = await fetch(`${IDENTITY_BASE}/v1/auth/login?${params}`);
  if (!res.ok) throw new Error('Failed to get login URL');
  return res.json();
}

/**
 * Exchange Azure AD tokens for internal Attain tokens.
 * This is step 2 of the SPA flow: browser already exchanged the code
 * with Azure AD directly (using PKCE), now we send the Azure tokens
 * to the Identity API.
 *
 * ⚠️ Returns ONE OF TWO disjoint shapes (RFC-0510 W15): a token pair, or an
 * org-selection challenge when the operator holds more than one active
 * membership. Callers MUST narrow with `requiresOrgSelection` before reading
 * `access_token`.
 */
export async function exchangeAzureTokens(
  accessToken: string,
  idToken: string,
): Promise<AzureTokenExchangeResult> {
  const res = await fetch(`${IDENTITY_BASE}/v1/auth/token-from-azure`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ access_token: accessToken, id_token: idToken }),
  });
  if (!res.ok) throw new Error('Failed to exchange Azure tokens');
  return res.json();
}

export async function getMe(accessToken: string): Promise<MeResponse> {
  const res = await fetch(`${IDENTITY_BASE}/v1/auth/me`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error('Failed to get user');
  return res.json();
}

export async function refreshToken(token: string): Promise<TokenResponse> {
  const res = await fetch(`${IDENTITY_BASE}/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: token }),
  });
  if (!res.ok) throw new Error('Failed to refresh token');
  return res.json();
}
